/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-empty-function */
import CollapsibleContentSection from "components/company/CollapsibleContentSection";
import BrazilianCompanyFinancials from "components/company/financials/brazilian/BrazilianCompanyFinancials";
import BrazilianIssuerFinancialHighlightsCollapsible from "components/company/sections/collapsible/BrazilianIssuerFinancialHighlightsCollapsible";
import DebtObligationsList from "components/company/sections/content/DebtObligationsList";
import EquityInterestsList from "components/company/sections/content/EquityInterestsList";
import FamilyRelationships from "components/company/sections/content/FamilyRelationships";
import IntangibleAssetsList from "components/company/sections/content/IntangibleAssetsList";
import Ipes from "components/company/sections/content/Ipes";
import KeyCustomersSection from "components/company/sections/content/KeyCustomersSection";
import Officers from "components/company/sections/content/Officers";
import OperatingSegmentsSection from "components/company/sections/content/OperatingSegmentsSection";
import PropertyPlantEquipmentList from "components/company/sections/content/PropertyPlantEquipmentList";
import TotalCompensations from "components/company/sections/content/TotalCompensations";
import ContentContainer from "components/company/sections/ContentContainer";
import { ContentSection } from "components/company/sections/ContentSection";
import ContentSectionBreadcrumbs from "components/company/sections/ContentSectionBreadcrumbs";
import ContentSectionDescriptionLine from "components/company/sections/ContentSectionDescriptionLine";
import ContentSectionHeading from "components/company/sections/ContentSectionHeading";
import ContentSectionLabeledCounters from "components/company/sections/summary/ContentSectionLabeledCounters";
import DebtObligationsSummary from "components/company/sections/summary/DebtObligationsSummary";
import EquityInterestsSummary from "components/company/sections/summary/EquityInterestsSummary";
import hasOneCompensation from "components/company/sections/summary/hasOneCompensation";
import MostRecentRelevantIpeSummary from "components/company/sections/summary/MostRecentRelevantIpeSummary";
import PropertyPlantEquipmentSummary from "components/company/sections/summary/PropertyPlantEquipmentSummary";
import TotalCompensationsSummary from "components/company/sections/summary/TotalCompensationsSummary";
import StaticContentSection from "components/company/StaticContentSection";
import {
  BrazilianCompanyIssuerGovernance,
  BrazilianCompanyIssuerHeaderCard,
  BrazilianCompanyIssuerRatings,
} from "components/company/v2";
import BrazilianCompanyCover from "components/company/v2/BrazilianCompanyCover";
import BrazilianCompanySubsidiaries from "components/company/v2/BrazilianCompanySubsidiaries";
import CvmRegistrationDateCard from "components/company/v2/cards/CvmRegistrationDateCard";
import EmployeesCountCard from "components/company/v2/cards/EmployeesCountCard";
import FoundedYearCard from "components/company/v2/cards/FoundedYearCard";
import HeadquartersCard from "components/company/v2/cards/HeadquartersCard";
import IbovespaCard from "components/company/v2/cards/IbovespaCard";
import MarketCapCard from "components/company/v2/cards/MarketCapCard";
import NextUpcomingCalendarEventCard from "components/company/v2/cards/NextUpcomingCalendarEventCard";
import ImpostoDeRenda from "components/company/v2/ImpostoDeRenda";
import YouTubeVideos from "components/company/v2/YouTubeVideos";
import AssetDiscussion from "components/discussions/AssetDiscussion";
import Liquidity from "components/liquidity/Liquidity";
import BackToTop from "components/qol/BackToTop";
import Rating from "components/rating/Rating";
import TwoPassRender from "components/shared/TwoPassRender";
import conditionalCounters from "components/utils/conditionalCounters";
import { useAmplitude } from "lib/amplitude/Amplitude";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import useSelfie from "lib/hooks/useSelfie";
import { palette } from "lib/miolo";
import TickerSymbol from "lib/securities/TickerSymbol";
import getBaseUrl from "lib/utils/getBaseUrl";
import { sum } from "lodash";
import Head from "next/head";
import Link from "next/link";
import { useCallback, useEffect, useState } from "react";
import { jsonLdScriptProps } from "react-schemaorg";
import type { Corporation } from "schema-dts";
import { twMerge } from "tailwind-merge";
import { IAssetNamespace, IAssetType } from "types/graphqlTypes";
import BrazilianDividendsCollapsible from "../sections/collapsible/BrazilianIssuerDividendsCollapsible";
import CorporateActionsCollapsible from "../sections/collapsible/CorporateActionsCollapsible";

interface IBrazilianCompanyPageProps {
  issuer: IBrazilianCompanyIssuerTopLevelFragment;
  tickerSymbol: string;
}

export default function BrazilianCompanyPage({ issuer, tickerSymbol }: IBrazilianCompanyPageProps) {
  const user = useSelfie();
  const amplitude = useAmplitude();
  const [activeContentSections, setActiveContentSections] = useState<ContentSection[]>([]);

  const toggleActiveContentSection = useCallback(
    (nextContentSection: ContentSection) => {
      return () => {
        if (activeContentSections.includes(nextContentSection)) {
          amplitude.logEvent("Closed Brazilian Company content section", {
            contentSection: nextContentSection,
          });
          setActiveContentSections(
            activeContentSections.filter((candidate) => {
              return candidate !== nextContentSection;
            }),
          );
        } else {
          amplitude.logEvent("Opened Brazilian Company content section", {
            contentSection: nextContentSection,
          });
          setActiveContentSections(activeContentSections.concat(nextContentSection));
        }
      };
    },
    [activeContentSections],
  );

  const isPremium = !!user?.isPremium;
  const isContentSectionActive = useCallback(
    (contentSection: ContentSection) => activeContentSections.includes(contentSection),
    [activeContentSections],
  );

  useEffect(() => {
    amplitude.logEvent("Viewed issuer", {
      category: "Ações",
      issuerId: issuer._id,
      tickerSymbol: tickerSymbol.toUpperCase(),
      name: issuer.name,
      sector: issuer.sector,
    });
  }, [issuer._id]);

  return (
    <div>
      <BackToTop />

      <BrazilianCompanyPageHead issuer={issuer} tickerSymbol={tickerSymbol} />

      <BrazilianCompanyCover issuer={issuer} />

      <div className="mx-auto max-w-7xl space-y-8 px-4 pb-16 xl:px-0">
        <div className="relative mt-16 space-y-8 rounded-xl bg-white px-4 pt-8 pb-8 shadow xl:mt-32">
          <div className="px-4">
            <BrazilianCompanyIssuerHeaderCard issuer={issuer} />
          </div>

          <div className="grid grid-cols-1 gap-8 xl:grid-cols-2">
            {/* Space between left-column items */}
            <div className="space-y-7">
              {user && (
                <div className="w-full px-4">
                  <h6 className="font-aeonik text-sm font-bold uppercase tracking-wide text-gray-600">
                    Avaliação dos Investidores
                  </h6>

                  <div className="mt-2">
                    <Rating
                      issuer={issuer}
                      rankingPlacement={issuer.overallRankingPlacement}
                      rankingRatingSummary={issuer.overallRankingRatingSummary}
                    />
                  </div>
                </div>
              )}

              <BrazilianCompanyIssuerGovernance issuer={issuer} />
            </div>

            <div className="space-y-8">
              {issuer.bookkeeper && <ImpostoDeRenda issuer={issuer} />}

              {(issuer.glassdoorRatings !== null || issuer.reclameAquiRatings !== null) && (
                <BrazilianCompanyIssuerRatings issuer={issuer} />
              )}
            </div>
          </div>

          {issuer.subsidiaries.length > 0 && (
            <div className="px-4">
              <BrazilianCompanySubsidiaries subsidiaries={issuer.subsidiaries.slice(0, 4)} />
            </div>
          )}
        </div>

        {/* Page delimiters */}
        <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {issuer.marketCapitalization && issuer.marketCapitalization.totalCapitalization > 0 && (
            <MarketCapCard issuer={issuer} />
          )}
          <FoundedYearCard issuer={issuer} />
          <CvmRegistrationDateCard issuer={issuer} />
          {issuer.b3IndexParticipation && issuer.b3IndexParticipation.stockParticipationPercent !== 0 && (
            <IbovespaCard issuer={issuer} />
          )}
          <HeadquartersCard issuer={issuer} />
          <EmployeesCountCard issuer={issuer} />
          {issuer.nextUpcomingCalendarEvent && <NextUpcomingCalendarEventCard issuer={issuer} />}
        </div>

        <div className="w-full">
          <StaticContentSection primaryColor={palette.oceanGreen} css={{ rowGap: 12, alignItems: "center" }}>
            <div className="col-span-7 sm:col-span-6 md:col-span-5 lg:col-span-4">
              <ContentSectionHeading>Valuation</ContentSectionHeading>
              <ContentSectionBreadcrumbs segments={["Informações Financeiras", "Indicadores Financeiros"]} />
            </div>

            <div className="col-span-1" />

            <div className="col-span-12 sm:col-span-5 md:col-span-6 lg:col-span-7">
              <div className="flex space-x-4">
                {issuer.securities
                  .filter((security) => {
                    return !TickerSymbol.isUnitStockSymbol(security.tickerSymbol);
                  })
                  .map((security) => {
                    const linkHref = `/br/${security.tickerSymbol.toLowerCase()}/valuation`;
                    const linkAs = `/[country]/[tickerSymbol]/valuation`;

                    return (
                      <Link
                        key={security.tickerSymbol}
                        as={linkHref}
                        href={linkAs}
                        className={twMerge(
                          "flex rounded-full bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-800",
                          "border border-transparent",
                          "hover:border-emerald-600",
                        )}
                      >
                        {security.tickerSymbol}
                      </Link>
                    );
                  })}
              </div>
            </div>
          </StaticContentSection>
        </div>

        <div>
          <CollapsibleContentSection
            title="Comunicados"
            isExpanded={isContentSectionActive(ContentSection.COMUNICADOS)}
            onToggleCollapsed={toggleActiveContentSection(ContentSection.COMUNICADOS)}
            primaryColor={palette.viridian}
            renderSubtitle={() => <ContentSectionDescriptionLine description="Últimos comunicados publicados na CVM" />}
            renderContentSummary={() => {
              if (!issuer.mostRecentRelevantIPE) {
                return null;
              }

              return <MostRecentRelevantIpeSummary mostRecentRelevantIPE={issuer.mostRecentRelevantIPE} />;
            }}
            renderContent={() => (
              <ContentContainer>
                <Ipes issuer={issuer} />
              </ContentContainer>
            )}
          />
        </div>

        <div className="w-full">
          <CorporateActionsCollapsible
            tickerSymbols={issuer.securities.map((node) => node.tickerSymbol)}
            isExpanded={isContentSectionActive(ContentSection.CORPORATE_ACTIONS)}
            onToggleCollapsed={toggleActiveContentSection(ContentSection.CORPORATE_ACTIONS)}
          />
        </div>

        <div className="space-y-9">
          <CollapsibleContentSection
            title="Administração"
            isExpanded={isContentSectionActive(ContentSection.ADMINISTRACAO)}
            isPremiumSection={true}
            onToggleCollapsed={toggleActiveContentSection(ContentSection.ADMINISTRACAO)}
            primaryColor={palette.mariner}
            renderSubtitle={() => <ContentSectionBreadcrumbs segments={["Governança", "Assembléia e Administração"]} />}
            renderContentSummary={() => {
              const officersCounts = conditionalCounters(issuer.officers, {
                boardOfOfficers: ({ board }) => board === "Pertence apenas ao Conselho de Administração",
                boardOfDirectors: ({ board }) => board === "Pertence apenas à Diretoria",
                fiscalCouncil: ({ board }) => board === "Conselho Fiscal",
              });

              return (
                <ContentSectionLabeledCounters
                  counters={[
                    { label: "Diretoria", totalCount: officersCounts.boardOfDirectors },
                    { label: "Administração", totalCount: officersCounts.boardOfOfficers },
                    { label: "Fiscal", totalCount: officersCounts.fiscalCouncil },
                  ]}
                />
              );
            }}
            renderContent={() => (
              <ContentContainer>
                <Officers issuer={issuer} />
              </ContentContainer>
            )}
          />

          {issuer.familyRelationships.length > 0 && (
            <CollapsibleContentSection
              title="Relações Familiares"
              isExpanded={isContentSectionActive(ContentSection.RELACOES_FAMILIARES)}
              isPremiumSection={true}
              onToggleCollapsed={toggleActiveContentSection(ContentSection.RELACOES_FAMILIARES)}
              primaryColor={palette.mariner}
              renderSubtitle={() => (
                <ContentSectionBreadcrumbs segments={["Governança", "Assembléia e Administração"]} />
              )}
              renderContent={() => (
                <ContentContainer>
                  <FamilyRelationships issuer={issuer} />
                </ContentContainer>
              )}
            />
          )}

          {issuer.financials.yearly.length > 0 && hasOneCompensation(issuer.totalCompensations, issuer.financials) && (
            <CollapsibleContentSection
              title="Remuneração dos Administradores"
              isExpanded={isContentSectionActive(ContentSection.REMUNERACAO_DOS_ADMINISTRADORES)}
              isPremiumSection={true}
              onToggleCollapsed={toggleActiveContentSection(ContentSection.REMUNERACAO_DOS_ADMINISTRADORES)}
              primaryColor={palette.mariner}
              renderSubtitle={() => (
                <ContentSectionBreadcrumbs segments={["Governança", "Assembléia e Administração"]} />
              )}
              renderContentSummary={() => (
                <TotalCompensationsSummary
                  totalCompensations={issuer.totalCompensations}
                  financials={issuer.financials}
                />
              )}
              renderContent={() => (
                <div className="space-y-4">
                  <ContentContainer className="overflow-scroll bg-white p-0">
                    <TotalCompensations totalCompensations={issuer.totalCompensations} financials={issuer.financials} />
                  </ContentContainer>
                  <div className="flex justify-center font-aeonik text-sm font-normal tracking-wide">
                    <p className="rounded-xl border border-slate-200 bg-slate-100 px-4 py-2 text-slate-600">
                      Os valores de remuneração acima estão em <strong>Reais</strong>. {"Lucro e Receita em "}
                      <strong>milhões</strong>.
                    </p>
                  </div>
                </div>
              )}
            />
          )}

          {issuer.propertyPlantEquipment.length > 0 && (
            <CollapsibleContentSection
              title="Ativos Imobilizados"
              isExpanded={isContentSectionActive(ContentSection.ATIVOS_IMOBILIZADOS)}
              isPremiumSection={true}
              onToggleCollapsed={toggleActiveContentSection(ContentSection.ATIVOS_IMOBILIZADOS)}
              primaryColor={palette.mariner}
              renderSubtitle={() => <ContentSectionBreadcrumbs segments={["Ativos Relevantes"]} />}
              renderContentSummary={() => (
                <PropertyPlantEquipmentSummary propertyPlantEquipment={issuer.propertyPlantEquipment} />
              )}
              renderContent={() => (
                <ContentContainer>
                  <PropertyPlantEquipmentList propertyPlantEquipment={issuer.propertyPlantEquipment} />
                </ContentContainer>
              )}
            />
          )}

          {issuer.intangibleAssets.length > 0 && (
            <div>
              <CollapsibleContentSection
                title="Ativos Intangíveis"
                isExpanded={isContentSectionActive(ContentSection.ATIVOS_INTANGIVEIS)}
                isPremiumSection={true}
                onToggleCollapsed={toggleActiveContentSection(ContentSection.ATIVOS_INTANGIVEIS)}
                primaryColor={palette.mariner}
                renderSubtitle={() => <ContentSectionBreadcrumbs segments={["Ativos Relevantes"]} />}
                renderContentSummary={() => {
                  const types = [...new Set(issuer.intangibleAssets.map(({ type }) => type))];

                  let counters = types
                    .map((type) => {
                      const totalCount = issuer.intangibleAssets.filter((candidate) => candidate.type === type).length;
                      return {
                        label: type,
                        totalCount,
                      };
                    })
                    .sort((a, b) => {
                      return b.totalCount - a.totalCount;
                    })
                    .map(({ label, totalCount }) => {
                      return {
                        // "Domínios" is a shorter display name for "Nome de domínio na internet"
                        label: label === "Nome de domínio na internet" ? "Domínios" : label,
                        totalCount,
                      };
                    });

                  if (counters.length > 3) {
                    // If there's more than 3 trackers, sum everything else into "Outros"
                    counters = [
                      ...counters.slice(0, 3),
                      {
                        label: "Outros",
                        totalCount: sum(counters.slice(3).map(({ totalCount }) => totalCount)),
                      },
                    ];
                  }

                  return <ContentSectionLabeledCounters counters={counters} />;
                }}
                renderContent={() => (
                  <ContentContainer>
                    <IntangibleAssetsList intangibleAssets={issuer.intangibleAssets} />
                  </ContentContainer>
                )}
              />
            </div>
          )}

          {issuer.equityInterests.length > 0 && (
            <div>
              <CollapsibleContentSection
                title="Participações em Sociedades"
                isExpanded={isContentSectionActive(ContentSection.PARTICIPACOES_EM_SOCIEDADES)}
                isPremiumSection={true}
                onToggleCollapsed={toggleActiveContentSection(ContentSection.PARTICIPACOES_EM_SOCIEDADES)}
                primaryColor={palette.mariner}
                renderSubtitle={() => <ContentSectionBreadcrumbs segments={["Ativos Relevantes"]} />}
                renderContentSummary={() => <EquityInterestsSummary equityInterests={issuer.equityInterests} />}
                renderContent={() => (
                  <ContentContainer>
                    <EquityInterestsList equityInterests={issuer.equityInterests} />
                  </ContentContainer>
                )}
              />
            </div>
          )}
        </div>

        {issuer.operatingSegments && (
          <div className="w-full">
            <OperatingSegmentsSection
              operatingSegments={issuer.operatingSegments}
              operatingSegmentsUrl={issuer.operatingSegmentsUrl}
              financials={issuer.financials}
            />
          </div>
        )}

        {isPremium && issuer.keyCustomers && (
          <div className="w-full">
            <KeyCustomersSection keyCustomers={issuer.keyCustomers} />
          </div>
        )}

        {issuer.debtObligations.length > 0 &&
          issuer.financials.yearly.every(
            ({ __typename }) => __typename !== "YearlyBrazilianBankFinancialStatementRow",
          ) && (
            <div>
              <CollapsibleContentSection
                title="Endividamento"
                isPremiumSection={true}
                isExpanded={isContentSectionActive(ContentSection.ENDIVIDAMENTO)}
                onToggleCollapsed={toggleActiveContentSection(ContentSection.ENDIVIDAMENTO)}
                primaryColor={palette.mojo}
                renderSubtitle={() => (
                  <ContentSectionBreadcrumbs segments={["Informações Financeiras", "Vencimentos da Dívida"]} />
                )}
                renderContentSummary={() => <DebtObligationsSummary debtObligations={issuer.debtObligations} />}
                renderContent={() => (
                  <ContentContainer className="overflow-scroll p-0">
                    <DebtObligationsList debtObligations={issuer.debtObligations} />
                  </ContentContainer>
                )}
              />
            </div>
          )}

        <div className="w-full">
          <BrazilianDividendsCollapsible
            tickerSymbol={issuer.securities[0].tickerSymbol}
            issuer={issuer}
            isExpanded={isContentSectionActive(ContentSection.DIVIDENDOS)}
            onToggleCollapsed={toggleActiveContentSection(ContentSection.DIVIDENDOS)}
          />
        </div>

        <div className="space-y-9">
          <div>
            <BrazilianIssuerFinancialHighlightsCollapsible
              issuer={issuer}
              isPremium={isPremium}
              isExpanded={isContentSectionActive(ContentSection.DESTAQUES_FINANCEIROS)}
              onToggleCollapsed={toggleActiveContentSection(ContentSection.DESTAQUES_FINANCEIROS)}
            />
          </div>
        </div>

        {issuer.securities && issuer.securities.length > 0 && issuer.securities.some(({ hasQuotes }) => hasQuotes) && (
          <div className="mx-auto max-w-[1140px]">
            <Liquidity
              key={issuer._id}
              asset={issuer}
              namespace={IAssetNamespace.Bra}
              tickerSymbols={issuer.securities
                .filter((security) => security.hasQuotes)
                .map((security) => security.tickerSymbol)}
            />
          </div>
        )}

        <BrazilianCompanyFinancials issuer={issuer} />

        {issuer.youtubeVideos.length > 0 && (
          <YouTubeVideos
            key={issuer._id}
            youtubeVideos={isPremium ? issuer.youtubeVideos : issuer.youtubeVideos.slice(0, 1)}
          />
        )}

        <TwoPassRender>
          {() => <AssetDiscussion assetType={IAssetType.BrazilianCompany} assetId={issuer._id} />}
        </TwoPassRender>
      </div>
    </div>
  );
}

interface IBrazilianCompanyPageHeadProps {
  issuer: NonNullable<IBrazilianCompanyIssuerTopLevelFragment>;
  tickerSymbol: string;
}

function BrazilianCompanyPageHead({ issuer, tickerSymbol }: IBrazilianCompanyPageHeadProps) {
  const canonicalURL = `${getBaseUrl()}/br/${issuer.tickerSymbolPrefix}`.toLowerCase();
  const pageTitle = `${issuer.name} - ${issuer.tickerSymbolPrefix} | Fundamentei`;
  const openGraphTitle = `${tickerSymbol} (${issuer.name}) - Análise Fundamentalista`;

  return (
    <Head>
      <title>{pageTitle}</title>

      <meta name="title" content={pageTitle} key="title" />
      <link rel="canonical" href={canonicalURL} key="canonical" />

      <meta property="og:title" key="og:title" itemProp="name" content={openGraphTitle} />
      <meta property="og:headline" key="og:headline" itemProp="headline" content={openGraphTitle} />
      <meta
        key="description"
        name="description"
        content={`Análise fundamentalista completa e atualizada com os principais indicadores, dados e balanços (DRE, Endividamento, Caixa) dentre outros da ${issuer.name} (${tickerSymbol})`}
      />
      <meta
        key="og:description"
        property="og:description"
        itemProp="description"
        content={`Análise fundamentalista completa e atualizada com os principais indicadores, dados e balanços (DRE, Endividamento, Caixa) dentre outros da ${issuer.name} (${tickerSymbol})`}
      />
      <meta property="og:url" key="og:url" itemProp="url" content={canonicalURL} />
      <meta property="og:type" key="og:type" content="website" />
      <meta
        key="og:image"
        property="og:image"
        itemProp="image"
        content={`${getBaseUrl()}/static/branding/wordmark/White%20on%20Blue@4x.jpg`}
      />
      <meta key="og:locale" property="og:locale" content="pt_BR" />

      <script
        {...jsonLdScriptProps<Corporation>({
          "@context": "https://schema.org",
          "@type": "Corporation",
          tickerSymbol,
          name: issuer.name,
          description: issuer.businessDescription,
          taxID: issuer.cnpj,
          foundingDate: `${issuer.foundedYear}`,
          numberOfEmployees: issuer.employeeNumbers
            ? {
                "@type": "QuantitativeValue",
                value: issuer.employeeNumbers?.totalOfEmployees,
              }
            : undefined,
          logo: issuer.niceLogo?.url || issuer.standardLogo?.url,
          url: issuer.investorRelationsUrl || issuer.website || canonicalURL,
        })}
      />
    </Head>
  );
}
